<script setup>
import {ref, computed, inject, onBeforeUpdate, watch} from "vue";
import store from "@/store";
import {EntityType} from "@/components/common/types/entity";
import {DataState} from "@/components/common/types/data";
import {useRoute, useRouter} from "vue-router/composables";
import ProgressCircular from "@/components/common/ProgressCircular.vue";
import AddressNotFoundInfo from "@/components/address/AddressNotFoundInfo.vue";
import TransactionsFilter from "@/components/transaction/TransactionsFilter.vue";
import DataProvider from "@/components/common/DataProvider.vue";
import {isOnSplitView} from "@/utils";

const entityId = inject('entityId')
const clusterInfoState = inject('clusterInfoState', undefined)
const clusterInfo = inject('clusterInfo', undefined)

const entityInfo = computed(() => clusterInfo?.value ?? store.getters.addressInfo)

store.dispatch('initTransactions')

const route = useRoute()
const router = useRouter()

//Items-per-page ("itemsPerPage") control, shareable via the route query.
const DEFAULT_ITEMS_PER_PAGE = 20
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100]
const CUSTOM_OPTION = 'custom'
const itemsPerPageSelectItems = [
    {text: '10', value: 10},
    {text: '20', value: 20},
    {text: '50', value: 50},
    {text: '100', value: 100},
    {text: 'Custom', value: CUSTOM_OPTION},
]

const initialItemsPerPage = Number(route.query.itemsPerPage) || DEFAULT_ITEMS_PER_PAGE
//Authoritative value sent to the BE `itemsPerPage` param.
const itemsPerPage = ref(initialItemsPerPage)
const isCustomItemsPerPage = ref(!ITEMS_PER_PAGE_OPTIONS.includes(initialItemsPerPage))
const selectedItemsPerPageOption = ref(isCustomItemsPerPage.value ? CUSTOM_OPTION : initialItemsPerPage)
const customItemsPerPage = ref(initialItemsPerPage)

const applyItemsPerPage = (value) => {
    const parsed = Number(value)
    //No upper cap; only reject non-positive/invalid input.
    if (!Number.isInteger(parsed) || parsed < 1) {
        return
    }
    itemsPerPage.value = parsed
    //Persist in the route query (merge, don't clobber other params) and reset to page 1.
    const query = {...route.query, itemsPerPage: parsed}
    delete query.page
    router.replace({query}).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
            throw error
        }
    })
    if (tx_pagination.value !== 1) {
        //Resetting the page triggers the tx_pagination watcher, which refetches.
        store.commit('TX_PAGINATION', 1)
    } else {
        fetchTransactions()
    }
}

const onItemsPerPageOptionChange = (value) => {
    isCustomItemsPerPage.value = value === CUSTOM_OPTION
    selectedItemsPerPageOption.value = value
    applyItemsPerPage(isCustomItemsPerPage.value ? customItemsPerPage.value : value)
}

const onCustomItemsPerPageChange = (value) => {
    customItemsPerPage.value = value
    applyItemsPerPage(value)
}

const isAddressView = computed(() => (route.params.address && !isOnSplitView(route)) || route.params.detailAddress)
const entityType = computed(() => isAddressView.value ? EntityType.Address : EntityType.Cluster)

const loading = computed(() => {
    if (isAddressView.value) {
        return store.getters.addressLoading
    }
    return clusterInfoState.value === DataState.Loading || clusterInfoState.value === DataState.Initial
})

const emit = defineEmits(['component-loaded-event'])

onBeforeUpdate(() => {
    //Update visual slider (workaround)
    emit("component-loaded-event")
})

const filter = ref()

const tx_pagination = computed(() => store.getters.tx_pagination)
const currency = computed(() => store.getters.currency)
const fetchTransactions = () => {
    store.dispatch('loadTransactionsByEntity', {
        entityId: entityId.value,
        currency: currency.value.unit,
        pagination: {
            page: tx_pagination.value,
            itemsPerPage: itemsPerPage.value,
        },
        filter: { ...filter.value },
        entityTypeParam: entityType.value
    })
}

watch(tx_pagination, async () => {
    fetchTransactions()
})

const fetchGeo = () => {
    store.dispatch('loadTransactionsGeo', {
        entityId: entityId.value,
        currency: currency.value.unit,
        filter: { ...filter.value },
        entityTypeParam: entityType.value
    })
}

const geoTabActive = computed(() => route.path.slice(-3) === 'geo')
let previousGeoFilter = null
const geoFilterChanged = () => JSON.stringify(previousGeoFilter) !== JSON.stringify(filter.value)

let previousTransactionsFilter = undefined
const transactionFilterChanged = () => JSON.stringify(previousTransactionsFilter) !== JSON.stringify(filter.value)

const getDataForCurrentSubview = () => {
    if (geoTabActive.value && geoFilterChanged()) {
        fetchGeo()
        previousGeoFilter = filter.value
    } else if (transactionFilterChanged()) {
        fetchTransactions()
        previousTransactionsFilter = filter.value
    }
}

const getRelativeDestination = (dest) => {
    const base = route.fullPath.slice(0, route.fullPath.lastIndexOf('/transactions'))
    return { path: base + dest, query: route.query }
}

const tab = ref()
watch(tab, () => {
    getDataForCurrentSubview()
})

const tabValue = computed(() => {
    const path = route.path
    const fullPath = route.fullPath
    return path[path.length - 1] !== '/' ?
        `${path}/${fullPath.substring(path.length)}` :
        fullPath
})

const handleTabChange = () => {
    tab.value = tabValue.value
}

</script>

<template>
    <div class="height100 scroll-y">
        <div v-if="loading">
            <ProgressCircular class="pt-10" :color="currency.unit"/>
        </div>
        <v-container v-else-if="!entityInfo || !entityId">
            <div>
                <AddressNotFoundInfo
                    class="my-2"
                    :address="entityId"
                    :message="'Entity with the specified name does not exist!'"
                />
            </div>
        </v-container>

        <div v-else>
            <h1>Transactions</h1>
            <v-divider/>
            <v-divider class="mb-3"/>
            <TransactionsFilter
                v-model="filter"
                @on-filter-event="getDataForCurrentSubview"
            />
            <div class="d-flex align-end">
                <v-tabs
                    v-if="currency.typeFamily !== 'ETH'"
                    :value="tabValue"
                    @change="handleTabChange"
                    :slider-color="currency.hexColor">
                    <v-tab :to="getRelativeDestination('/transactions')">
                        Transactions
                    </v-tab>
                    <v-tab :to="getRelativeDestination('/transactions/geo')">
                        Geolocations
                    </v-tab>
                </v-tabs>
                <v-spacer/>
                <div v-if="!geoTabActive" class="d-flex align-center pb-1" style="gap: 8px">
                    <v-select
                        :items="itemsPerPageSelectItems"
                        :value="selectedItemsPerPageOption"
                        @change="onItemsPerPageOptionChange"
                        label="Items per page"
                        dense
                        hide-details
                        outlined
                        style="max-width: 130px"
                    />
                    <v-text-field
                        v-if="isCustomItemsPerPage"
                        :value="customItemsPerPage"
                        @change="onCustomItemsPerPageChange"
                        type="number"
                        min="1"
                        label="Custom"
                        dense
                        hide-details
                        outlined
                        style="max-width: 110px"
                    />
                </div>
            </div>
            <v-divider/>
            <div class="mt-3 width100">
                <DataProvider name="entityType" :data="entityType">
                    <router-view/>
                </DataProvider>
            </div>
        </div>
    </div>
</template>